import { useCallback } from "react"
import { useComputed } from "@preact/signals-react"
import { useAppToast } from "@ui-seeker/app"
import { capitalize, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { formData, loadingRow, tableData } from "../@signals"


export default function useAdminBrandActions(row) {
    const { showToast } = useAppToast()

    const isClicked = useComputed(() => row.brandID === formData.value.brandID)
    const isLoading = useComputed(() => row.brandID === loadingRow.value)

    const updateSwitch = useCallback((field, value) => {
        loadingRow.value = row.brandID
        // A toggle only changes a brand field (validated, ...). It must NOT
        // carry customers, or the backend would upsert customer_brand rows and
        // reset their availability flags. Omit customerIDs so the backend skips
        // the relation and only updates the brand.
        showToast.promise(postSubmit(`${BACKEND_APIS.BRANDS}/crud/update-brand`, { ...row, customerIDs: undefined, [field]: value }).then(res => {
            loadingRow.value = null
            tableData.value = tableData.value.map(dataRow => dataRow.brandID === res.brandID ? {
                ...dataRow,
                validated: res.validated
            } : dataRow)
        }).catch(() => {
            showToast.error()
            loadingRow.value = null
        }), {
            success: `${capitalize(field)} updated`,
            loading: "Updating..."
        })
    }, [row])

    // Toggle one availability flag for ONE customer of the brand. The payload
    // carries a lone `customerID` (an add, not a full set) so the backend does
    // NOT reconcile and the brand's other customers keep their rows, and it
    // omits `customerAvailability` so the flat flags below are what applies —
    // both of them, since the flat pair writes both columns.
    const updateAvailability = useCallback((customerID, field, value) => {
        const entry = (row.customerAvailability || []).find(item => String(item.customerID) === String(customerID)) || {}
        const flags = {
            siteNavigations: field === 'siteNavigations' ? value : !!entry.siteNavigations,
            banners: field === 'banners' ? value : !!entry.banners
        }

        loadingRow.value = row.brandID
        showToast.promise(postSubmit(`${BACKEND_APIS.BRANDS}/crud/update-brand`, {
            ...row,
            customerIDs: undefined,
            customerAvailability: undefined,
            customerID,
            ...flags
        }).then(res => {
            loadingRow.value = null
            // The response only carries the customer just written, so merge it
            // into the row's list instead of replacing it.
            const written = (res.customerAvailability || []).find(item => String(item.customerID) === String(customerID))
            tableData.value = tableData.value.map(dataRow => dataRow.brandID === res.brandID ? {
                ...dataRow,
                customerAvailability: (dataRow.customerAvailability || []).map(item =>
                    String(item.customerID) === String(customerID) ? { ...item, ...(written || flags) } : item
                )
            } : dataRow)
        }).catch(() => {
            showToast.error()
            loadingRow.value = null
        }), {
            success: `${capitalize(field === 'siteNavigations' ? 'site navigations' : field)} updated`,
            loading: "Updating..."
        })
    }, [JSON.stringify(row)])

    const handleClickUpdateCreate = useCallback(() => {
        formData.value = {
            ...row,
            customerIDs: row.customerIDs || [],
            customerAvailability: row.customerAvailability || []
        }
    }, [JSON.stringify(row)])

    const handleClickDelete = useCallback(() => {
        showToast.confirm("Are you sure you want to delete?", {
            onSubmit: () => {
                loadingRow.value = row.brandID
                // TODO: Add delete API endpoint once available
                showToast.promise(postSubmit(``, { brandID: row.brandID }).then(() => {
                    loadingRow.value = null
                    tableData.value = tableData.value.filter(dataRow => dataRow.brandID !== row.brandID)
                }).catch(() => {
                    showToast.error()
                    loadingRow.value = null
                }), {
                    success: "Brand deleted successfully",
                    loading: "Deleting..."
                })
            }
        })
    }, [row])

    return {
        isClicked: isClicked.value,
        isLoading: isLoading.value,

        updateSwitch,
        updateAvailability,
        handleClickUpdateCreate,
        handleClickDelete
    }
}
